import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {Table, TableBody, TableContainer} from '@mui/material';
import {useLocation, useSearchParams} from 'react-router-dom';

import {TableHeader, TablePagination, UsersTableFilter, UsersTableRow} from '../components/table';
import {usersTableHead} from '../app/config';
import {apiClient} from '../providers/apiClient';

import './screens.styles.scss';
import '../components/table/table.styles.scss';
import {useAuth} from "../hooks";
import useUsers from "../hooks/useUsers";
import {showModal} from "../app/slices/modalSlice";
import {useDispatch} from "react-redux";


const toUrlObj = (f) => {
    const o = {};
    if (f.pageSize) o.pageSize = String(f.pageSize);
    if (f.pageNumber) o.pageNumber = String(f.pageNumber);
    if (f.orderBy) o.orderBy = f.orderBy;
    if (f.orderDirection) o.orderDirection = f.orderDirection;
    if (f.search) o.search = f.search;
    return o;
};

const fromUrl = (sp) => {
    const num = (v, d) => {
        const n = Number(v);
        return Number.isFinite(n) && n > 0 ? n : d;
    };
    const dir = (v) => (v === "asc" || v === "desc" ? v : null);
    return {
        pageSize: num(sp.get("pageSize"), 6),
        pageNumber: num(sp.get("pageNumber"), 1),
        orderBy: sp.get("orderBy") || null,
        orderDirection: dir(sp.get("orderDirection")),
        search: sp.get("search"),
    };
};

const sameParams = (a, b) => {
    const A = Object.entries(a).filter(([, v]) => v !== null && v !== undefined && v !== "");
    const B = Object.entries(b).filter(([, v]) => v !== null && v !== undefined && v !== "");
    if (A.length !== B.length) return false;
    return A.every(([k, v]) => String(v) === String(b[k]));
};

const UsersScreen = () => {
    const [open, setOpen] = useState(null);
    const {isAdmin} = useAuth();
    const dispatch = useDispatch();

    const [searchParams, setSearchParams] = useSearchParams();
    const location = useLocation();


    const initial = useMemo(() => fromUrl(searchParams), []);

    const {
        data: users,
        filter,
        setFilter,
        nextPage,
        previousPage,
        refresh,
        pagination,
    } = useUsers({...initial, isConsumer: false});

    useEffect(() => {
        const incoming = fromUrl(searchParams);
        const current = {
            pageSize: filter.pageSize,
            pageNumber: filter.pageNumber,
            orderBy: filter.orderBy,
            orderDirection: filter.orderDirection,
            search: filter.search,
        };


        if (!location.search) {
            setOpen(null);
            return;
        }

        if (!sameParams(incoming, toUrlObj(current))) {
            Object.entries(incoming).forEach(([k, v]) => setFilter(k, v));
        }

    }, [searchParams, location.search]);

    useEffect(() => {
        const desired = toUrlObj(filter);
        const current = Object.fromEntries(searchParams.entries());

        if (!sameParams(desired, current)) {
            const sp = new URLSearchParams(desired);
            setSearchParams(desired, {replace: true});
        }

    }, [
        filter.pageSize,
        filter.pageNumber,
        filter.orderBy,
        filter.orderDirection,
        searchParams,
        setSearchParams,
    ]);

    const beforeUrl = `${location.pathname}${location.search}`;

    const showOnPage = useCallback(
        (value) => {
            setFilter('pageSize', value);
            setFilter('pageNumber', 1);
        },
        [setFilter]
    );

    const handleRequestSort = useCallback(
        (event, property) => {
            const nextDir =
                filter.orderBy === property && filter.orderDirection === 'asc' ? 'desc' : 'asc';
            setFilter('orderBy', property);
            setFilter('orderDirection', nextDir);
        },
        [filter.orderBy, filter.orderDirection, setFilter]
    );

    const handleRowClick = useCallback((id) => {
        setOpen((curr) => (curr === id ? null : id));
    }, []);

    const handleStatusChange = useCallback(
        async (id) => {
            try {
                await apiClient.post(`/api/Account/${id}/changeStatus`);
                await refresh();
            } catch (e) {
                // eslint-disable-next-line no-console
                console.error(e);
            }
        },
        [refresh]
    );

    const handleRemoveRole = useCallback(
        async (id) => {
            try {
                await apiClient.post(`/api/Account/${id}/removeAdmin`);
                await refresh();
            } catch (e) {
                // eslint-disable-next-line no-console
                console.error(e);
            }
        },
        [refresh]
    );

    const handleDelete = useCallback(async (user) => {
        const modalParams = {
            title: 'User account deletion',
            message: `Are you sure that you want to delete ${user.fullName}?`,
            button: 'Delete',
            mode: 'normal',
            cancelButton: true,
            fallback: async () => {
                try {
                    await apiClient.delete(`/api/Account?id=${user.id}`);
                    await refresh();
                } catch (e) {
                    console.error(e);
                }
            }
        };

        dispatch(showModal(modalParams));
    }, [refresh, showModal]);

    return (
        <div className="fk-screen">
            <UsersTableFilter
                isCurrentUserAdmin={isAdmin}
                filter={filter}
                setFilter={setFilter}
                onSubmit={refresh}/>

            <TableContainer>
                <Table size="medium" className="fk-table">
                    <TableHeader
                        crud
                        items={usersTableHead}
                        orderBy={filter.orderBy}
                        order={filter.orderDirection}
                        onRequestSort={handleRequestSort}
                        screen="UsersScreen"
                    />

                    <TableBody>
                        {users.map((user) => (
                            <UsersTableRow
                                key={user.id}
                                user={user}
                                open={open === user.id}
                                onRowClick={handleRowClick}
                                onStatusChange={handleStatusChange}
                                onDelete={handleDelete}
                                onRemoveRole={handleRemoveRole}
                                isCurrentUserAdmin={isAdmin}
                                before={beforeUrl}
                            />
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            <TablePagination
                filter={pagination}
                handlePreviousPage={previousPage}
                handleNextPage={nextPage}
                showOnPage={showOnPage}
            />
        </div>
    );
};

export default UsersScreen;
